import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

export type Option = {
  label: string;
  value: string;
};

interface MultiSelectProps {
  options?: Option[] | null;
  value?: string[] | null;
  onChange: (value: string[]) => void;
  className?: string;
  placeholder?: string;
  isLoading?: boolean;
}

export function MultiSelect({
  options = [],
  value = [],
  onChange,
  className = "",
  placeholder = "Select items...",
  isLoading = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Ensure arrays are valid
  const safeOptions = React.useMemo(() => {
    return Array.isArray(options) ? options : [];
  }, [options]);

  const safeValue = React.useMemo(() => {
    return Array.isArray(value) ? value : [];
  }, [value]);

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return safeOptions;
    return safeOptions.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [safeOptions, searchQuery]);

  // Get selected items
  const selectedItems = React.useMemo(() => {
    return safeValue
      .map((v) => safeOptions.find((opt) => opt.value === v))
      .filter((item): item is Option => Boolean(item));
  }, [safeValue, safeOptions]);

  const handleSelect = React.useCallback(
    (itemValue: string) => {
      const newValue = safeValue.includes(itemValue)
        ? safeValue.filter((v) => v !== itemValue)
        : [...safeValue, itemValue];
      onChange(newValue);
    },
    [safeValue, onChange]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={isLoading}
          className={cn("w-full justify-between", className)}
        >
          <div className="flex flex-wrap gap-1">
            {selectedItems.length > 0 ? (
              selectedItems.map((item) => (
                <Badge
                  key={item.value}
                  variant="secondary"
                  className="mr-1 mb-1"
                >
                  {item.label}
                  <button
                    type="button"
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(item.value);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <span className="shrink-0 opacity-50">⌄</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="flex flex-col">
          <div className="flex items-center border-b p-2">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <div className="max-h-[200px] overflow-auto p-1">
            {isLoading ? (
              <div className="p-2 text-sm text-muted-foreground">
                Loading...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">
                No results found
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {filteredOptions.map((option) => {
                  const isSelected = safeValue.includes(option.value);
                  return (
                    <Button
                      key={option.value}
                      variant="ghost"
                      className={cn(
                        "justify-start gap-2 h-auto py-1.5",
                        isSelected && "bg-muted"
                      )}
                      onClick={() => handleSelect(option.value)}
                    >
                      <div
                        className={cn(
                          "h-4 w-4 border rounded-sm",
                          isSelected && "bg-primary border-primary"
                        )}
                      />
                      {option.label}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
